/** ADM-40 Paramètres métier · ADM-44 Journal d'audit (vérification de chaîne, export ★) · ADM-45 Demandes RGPD. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Modal, Pagination, SelectField, Table, TextField, TextareaField, useToast } from '@myron/design-system';
import { Download, Lock, ShieldCheck } from 'lucide-react';
import type { S } from '@myron/contracts';
import { ApiErreur, get, post, put } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete, MentionSimule, StatutBadge } from '../../../shared/ui/composants';
import { dateHeure, euros } from '../../../shared/ui/format';

// ───────────────────────────────── Paramètres ─────────────────────────────────
type Parametre = S<'Parametre'> & { valeur: unknown };
const LIBELLE_DOMAINE: Record<string, string> = { COMMERCIAL: 'Commercial', LOGISTIQUE: 'Logistique', INSCRIPTION: 'Inscription', SECURITE: 'Sécurité' };

function afficherValeur(p: Parametre) {
  if (p.type === 'BOOLEEN') return p.valeur ? 'Oui' : 'Non';
  if (p.type === 'CENTIMES') return euros(p.valeur as number);
  if (p.type === 'LISTE') return (p.valeur as unknown[]).join(', ');
  if (p.type === 'OBJET') return JSON.stringify(p.valeur);
  return String(p.valeur);
}

export function PageParametres() {
  const qc = useQueryClient();
  const toast = useToast();
  const { peut, aRole } = useSession();
  const q = useQuery({ queryKey: ['admin', 'parametres'], queryFn: () => get<Parametre[]>('/admin/parametres') });
  const [edition, setEdition] = useState<{ p: Parametre; brut: string } | null>(null);
  const [erreurLocale, setErreurLocale] = useState<string | null>(null);
  const modifiable = (p: Parametre) => peut('parametres.modifier') && (aRole('SUPER_ADMIN') || p.domaine === 'COMMERCIAL');
  const enregistrer = useMutation({
    mutationFn: ({ p, valeur }: { p: Parametre; valeur: unknown }) => put<Parametre>(`/admin/parametres/${encodeURIComponent(p.cle!)}`, { valeur }, { version: p.version }),
    onSuccess: (p) => { setEdition(null); void qc.invalidateQueries({ queryKey: ['admin', 'parametres'] }); toast({ ton: 'succes', titre: 'Paramètre modifié', texte: p.cle }); },
  });
  const ouvrir = (p: Parametre) => {
    setErreurLocale(null);
    enregistrer.reset();
    const brut = p.type === 'CENTIMES' ? String((p.valeur as number) / 100) : p.type === 'LISTE' ? (p.valeur as unknown[]).join(', ') : p.type === 'OBJET' ? JSON.stringify(p.valeur, null, 2) : String(p.valeur);
    setEdition({ p, brut });
  };
  const valider = () => {
    if (!edition) return;
    const { p, brut } = edition;
    let valeur: unknown;
    try {
      if (p.type === 'BOOLEEN') valeur = brut === 'true';
      else if (p.type === 'ENTIER') { valeur = Number(brut); if (!Number.isInteger(valeur) || (valeur as number) < 0) throw new Error('Entier positif attendu'); }
      else if (p.type === 'CENTIMES') { const n = Number(brut.replace(',', '.')); if (!Number.isFinite(n) || n < 0) throw new Error('Montant positif attendu'); valeur = Math.round(n * 100); }
      else if (p.type === 'LISTE') valeur = brut.split(',').map((x) => x.trim()).filter(Boolean);
      else valeur = JSON.parse(brut);
    } catch (e) { setErreurLocale(e instanceof Error ? e.message : 'Valeur invalide'); return; }
    setErreurLocale(null);
    enregistrer.mutate({ p, valeur });
  };
  return (
    <>
      <EntetePage titre="Paramètres métier" sousTitre="Toute modification exige une confirmation d’identité récente et est journalisée (avant / après)." />
      <EtatRequete requete={q} enfants={(l) => (
        <div className="my-pile my-pile--large">
          {Object.keys(LIBELLE_DOMAINE).filter((d) => l.some((p) => p.domaine === d)).map((d) => (
            <section key={d} aria-labelledby={`dom-${d}`} className="my-pile">
              <h2 id={`dom-${d}`} className="my-titre-4">{LIBELLE_DOMAINE[d]}</h2>
              <Table<Parametre>
                legende={`Paramètres — ${LIBELLE_DOMAINE[d]}`}
                cle={(p) => p.cle!}
                lignes={l.filter((p) => p.domaine === d)}
                colonnes={[
                  { cle: 'c', titre: 'Paramètre', rendu: (p) => <span className="my-pile my-pile--serre"><code className="my-code">{p.cle}</code><span className="my-petit my-secondaire">{p.description}</span></span> },
                  { cle: 'v', titre: 'Valeur', rendu: (p) => <strong>{afficherValeur(p)}</strong> },
                  { cle: 'm', titre: 'Modifié', rendu: (p) => (p.modifieLe ? <span className="my-petit">{dateHeure(p.modifieLe)}<br />{p.modifiePar}</span> : '—') },
                  { cle: 'a', titre: 'Actions', actions: true, rendu: (p) => (modifiable(p) ? <Button compact variante="secondaire" onClick={() => ouvrir(p)} aria-label={`Modifier ${p.cle}`}>Modifier</Button> : <Badge><Lock size={12} aria-hidden /> {peut('parametres.modifier') ? 'Super-admin' : 'Lecture'}</Badge>) },
                ]}
              />
            </section>
          ))}
        </div>
      )} />
      <Modal ouverte={!!edition} titre={`Modifier ${edition?.p.cle ?? ''}`} onFermer={() => setEdition(null)}
        actions={<><Button variante="tertiaire" onClick={() => setEdition(null)}>Annuler</Button><Button chargement={enregistrer.isPending} onClick={valider}>Enregistrer</Button></>}>
        {edition && (
          <div className="my-form">
            <p className="my-secondaire">{edition.p.description}</p>
            {edition.p.type === 'BOOLEEN' ? (
              <SelectField label="Valeur" value={edition.brut} onChange={(e) => setEdition({ ...edition, brut: e.target.value })} options={[{ value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }]} />
            ) : edition.p.type === 'OBJET' ? (
              <TextareaField label="Valeur (JSON)" value={edition.brut} onChange={(e) => setEdition({ ...edition, brut: e.target.value })} erreur={erreurLocale ?? undefined} />
            ) : (
              <TextField label={edition.p.type === 'CENTIMES' ? 'Valeur (€ HT)' : edition.p.type === 'LISTE' ? 'Valeurs (séparées par des virgules)' : 'Valeur'} inputMode={edition.p.type === 'LISTE' ? 'text' : 'decimal'} value={edition.brut} onChange={(e) => setEdition({ ...edition, brut: e.target.value })} erreur={erreurLocale ?? (enregistrer.error instanceof ApiErreur ? enregistrer.error.champs.valeur : undefined)} />
            )}
            {enregistrer.isError && !(enregistrer.error instanceof ApiErreur && enregistrer.error.champs.valeur) ? <ErreurFormulaire erreur={enregistrer.error} /> : null}
          </div>
        )}
      </Modal>
    </>
  );
}

// ───────────────────────────────── Journal d'audit ─────────────────────────────────
type EntreeAudit = S<'EntreeAudit'>;
type Verification = S<'VerificationChaineAudit'>;

export function PageAudit() {
  const toast = useToast();
  const { peut } = useSession();
  const [page, setPage] = useState(1);
  const [f, setF] = useState({ action: '', resultat: '', du: '', au: '' });
  const [detail, setDetail] = useState<EntreeAudit | null>(null);
  const [lien, setLien] = useState<{ url: string; lignes?: number } | null>(null);
  const q = useQuery({ queryKey: ['admin', 'audit', page, f], queryFn: () => get<{ donnees: EntreeAudit[]; total: number; page: number; taille: number }>('/admin/audit', { page, taille: 25, ...f }) });
  const verifier = useMutation({ mutationFn: () => post<Verification>('/admin/audit/verification') });
  const exporter = useMutation({
    mutationFn: () => post<{ url: string; lignes?: number }>('/admin/audit/export', { du: f.du ? `${f.du}T00:00:00.000Z` : '2000-01-01T00:00:00.000Z', au: f.au ? `${f.au}T23:59:59.999Z` : '2100-01-01T00:00:00.000Z' }),
    onSuccess: (r) => { setLien(r); toast({ ton: 'succes', titre: 'Export prêt', texte: `${r.lignes ?? ''} entrée(s)` }); },
  });
  const maj = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => { setF({ ...f, [k]: e.target.value }); setPage(1); };
  return (
    <>
      <EntetePage titre="Journal d’audit" sousTitre="Journal chaîné (SHA-256), en ajout seul. Les valeurs sensibles sont expurgées."
        actions={<>
          <Button variante="secondaire" icone={ShieldCheck} chargement={verifier.isPending} onClick={() => verifier.mutate()}>Vérifier l’intégrité</Button>
          {peut('audit.exporter') && <Button variante="secondaire" icone={Download} chargement={exporter.isPending} onClick={() => exporter.mutate()}>Exporter (CSV)</Button>}
        </>} />
      <div className="my-pile">
        {verifier.data && (verifier.data.integre
          ? <Alert ton="succes" titre="Chaîne intègre">{verifier.data.nbEntreesVerifiees} entrées vérifiées le {dateHeure(verifier.data.verifieeLe)}. <MentionSimule>SIMULÉ — journal stocké dans le navigateur</MentionSimule></Alert>
          : <Alert ton="erreur" titre="Rupture de chaîne détectée">Première entrée altérée : n° {verifier.data.premiereRuptureId}. Le journal a été modifié hors de l’application.</Alert>)}
        <ErreurFormulaire erreur={verifier.error ?? exporter.error} />
        {lien && <Alert ton="succes" titre="Export du journal"><a href={lien.url} download="myron-journal-audit.csv">Télécharger le fichier CSV</a> (formules neutralisées)</Alert>}
        <Card>
          <div className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
            <TextField label="Action contient" value={f.action} onChange={maj('action')} />
            <SelectField label="Résultat" value={f.resultat} onChange={maj('resultat')} options={[{ value: '', label: 'Tous' }, { value: 'SUCCES', label: 'Succès' }, { value: 'REFUS', label: 'Refus' }, { value: 'ECHEC', label: 'Échec' }]} />
            <TextField label="Du" type="date" value={f.du} onChange={maj('du')} />
            <TextField label="Au" type="date" value={f.au} onChange={maj('au')} />
          </div>
        </Card>
        <EtatRequete requete={q} vide={(r) => !r.donnees.length} titreVide="Aucune entrée" enfants={(r) => (
          <>
            <Table<EntreeAudit>
              legende="Entrées du journal d’audit"
              cle={(e) => String(e.id)}
              lignes={r.donnees}
              colonnes={[
                { cle: 'n', titre: 'N°', num: true, rendu: (e) => e.id },
                { cle: 'h', titre: 'Horodatage', rendu: (e) => dateHeure(e.horodatage) },
                { cle: 'ac', titre: 'Acteur', rendu: (e) => e.acteur },
                { cle: 'a', titre: 'Action', rendu: (e) => <code className="my-code">{e.action}</code> },
                { cle: 'r', titre: 'Ressource', rendu: (e) => e.ressourceType },
                { cle: 's', titre: 'Résultat', rendu: (e) => <Badge ton={e.resultat === 'SUCCES' ? 'succes' : e.resultat === 'REFUS' ? 'alerte' : 'erreur'}>{e.resultat === 'SUCCES' ? 'Succès' : e.resultat === 'REFUS' ? 'Refus' : 'Échec'}</Badge> },
                { cle: 'd', titre: 'Détail', actions: true, rendu: (e) => <Button compact variante="tertiaire" onClick={() => setDetail(e)} aria-label={`Détail de l’entrée ${e.id}`}>Détail</Button> },
              ]}
            />
            <Pagination page={r.page} total={r.total} taille={r.taille} onPage={setPage} />
          </>
        )} />
      </div>
      <Modal ouverte={!!detail} titre={`Entrée n° ${detail?.id ?? ''}`} onFermer={() => setDetail(null)} large actions={<Button variante="tertiaire" onClick={() => setDetail(null)}>Fermer</Button>}>
        {detail && (
          <dl className="my-dl">
            <dt>Action</dt><dd>{detail.action}</dd>
            <dt>Acteur</dt><dd>{detail.acteur} ({detail.acteurType})</dd>
            <dt>Ressource</dt><dd>{detail.ressourceType} {detail.ressourceId ?? ''}</dd>
            <dt>Avant</dt><dd><code className="my-code">{JSON.stringify(detail.avant ?? null)}</code></dd>
            <dt>Après</dt><dd><code className="my-code">{JSON.stringify(detail.apres ?? null)}</code></dd>
            <dt>Empreinte</dt><dd><code className="my-code">{detail.hash}</code></dd>
            <dt>Empreinte précédente</dt><dd><code className="my-code">{detail.hashPrecedent}</code></dd>
          </dl>
        )}
      </Modal>
    </>
  );
}

// ───────────────────────────────── RGPD ─────────────────────────────────
interface DemandeRgpd { id: string; utilisateur?: string; type: 'ACCES' | 'EFFACEMENT' | 'RECTIFICATION'; statut: string; recueLe: string; traiteeLe?: string; traiteePar?: string; precision?: string; export?: unknown; version: number }
const LIBELLE_TYPE_RGPD = { ACCES: 'Accès (export)', EFFACEMENT: 'Effacement', RECTIFICATION: 'Rectification' };

export function PageRgpd() {
  const qc = useQueryClient();
  const toast = useToast();
  const [statut, setStatut] = useState('RECUE');
  const [traitement, setTraitement] = useState<{ d: DemandeRgpd; decision: 'EXECUTER' | 'REFUSER' } | null>(null);
  const q = useQuery({ queryKey: ['admin', 'rgpd', statut], queryFn: () => get<DemandeRgpd[]>('/admin/rgpd/demandes', { statut: statut || undefined }) });
  const traiter = useMutation({
    mutationFn: ({ d, decision, motif }: { d: DemandeRgpd; decision: string; motif: string }) => post(`/admin/rgpd/demandes/${d.id}/traiter`, { decision, ...(motif ? { motif } : {}) }, { version: d.version }),
    onSuccess: () => { setTraitement(null); void qc.invalidateQueries({ queryKey: ['admin', 'rgpd'] }); toast({ ton: 'succes', titre: 'Demande traitée' }); },
  });
  return (
    <>
      <EntetePage titre="Demandes RGPD" sousTitre="Droits d’accès, de rectification et d’effacement. L’effacement anonymise le compte ; les pièces comptables sont conservées pseudonymisées." />
      <div className="my-pile">
        <div style={{ maxWidth: 320 }}><SelectField label="Statut" value={statut} onChange={(e) => setStatut(e.target.value)} options={[{ value: 'RECUE', label: 'À traiter' }, { value: 'TRAITEE', label: 'Traitées' }, { value: 'REFUSEE', label: 'Refusées' }, { value: '', label: 'Toutes' }]} /></div>
        <EtatRequete requete={q} vide={(l) => !l.length} titreVide="Aucune demande" enfants={(l) => (
          <Table<DemandeRgpd>
            legende="Demandes RGPD"
            cle={(d) => d.id}
            lignes={l}
            colonnes={[
              { cle: 'u', titre: 'Demandeur', rendu: (d) => d.utilisateur ?? '—' },
              { cle: 't', titre: 'Type', rendu: (d) => LIBELLE_TYPE_RGPD[d.type] },
              { cle: 'r', titre: 'Reçue le', rendu: (d) => dateHeure(d.recueLe) },
              { cle: 's', titre: 'Statut', rendu: (d) => <span className="my-pile my-pile--serre"><StatutBadge statut={d.statut === 'RECUE' ? 'EN_ATTENTE' : d.statut === 'TRAITEE' ? 'TERMINEE' : d.statut} />{d.traiteePar && <span className="my-petit my-secondaire">par {d.traiteePar}</span>}</span> },
              {
                cle: 'a', titre: 'Actions', actions: true, rendu: (d) => d.statut === 'RECUE' ? (
                  <span className="my-ligne" style={{ justifyContent: 'flex-end' }}>
                    <Button compact variante={d.type === 'EFFACEMENT' ? 'danger' : 'primaire'} onClick={() => setTraitement({ d, decision: 'EXECUTER' })} aria-label={`Exécuter la demande de ${d.utilisateur}`}>Exécuter</Button>
                    <Button compact variante="tertiaire" onClick={() => setTraitement({ d, decision: 'REFUSER' })}>Refuser</Button>
                  </span>
                ) : null,
              },
            ]}
          />
        )} />
      </div>
      <Confirmation
        ouverte={!!traitement}
        titre={traitement?.decision === 'REFUSER' ? 'Refuser la demande' : traitement?.d.type === 'EFFACEMENT' ? 'Anonymiser ce compte ?' : 'Exécuter la demande'}
        texte={traitement?.d.type === 'EFFACEMENT' && traitement.decision === 'EXECUTER' ? `Le compte ${traitement.d.utilisateur ?? ''} sera anonymisé et désactivé. Irréversible.` : traitement?.d.precision}
        libelleAction={traitement?.decision === 'REFUSER' ? 'Refuser' : 'Exécuter'}
        danger={traitement?.d.type === 'EFFACEMENT' || traitement?.decision === 'REFUSER'}
        motif={traitement?.decision === 'REFUSER' ? 'obligatoire' : undefined}
        onConfirmer={(motif) => traitement && traiter.mutate({ d: traitement.d, decision: traitement.decision, motif })}
        onFermer={() => setTraitement(null)}
        chargement={traiter.isPending}
        erreur={traiter.error}
      />
    </>
  );
}
